import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { Check, X } from 'lucide-react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Button } from '../../ui/catalyst/button';
import { Checkbox } from '../../ui/catalyst/checkbox';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../ui/catalyst/dropdown';
import { InputGroup } from '../../ui/catalyst/input';
import { ColumnFilterHeader } from './ColumnFilterHeader';
import { ColumnFilterTrigger } from './ColumnFilterTrigger';
import { DateRangeColumnFilter } from './DateRangeColumnFilter';
import { FilterSectionHeader } from './FilterSectionHeader';
import { PresetChips } from './PresetChips';
import { SortingSection } from './SortingSection';
import {
  ColumnFilter as ColumnFilterConfig,
  FilterOption,
  FilterValues,
} from './types';

interface ColumnFilterProps {
  /** Column identifier used for filter state */
  columnId: string;
  /** Display header for the column */
  header: string | ReactNode;
  /** Filter configuration */
  filter: ColumnFilterConfig;
  /** Current filter value */
  value: string | string[] | null | undefined;
  /** Filter change handler */
  onChange: (columnId: string, value: string | string[] | null) => void;
  /** Whether column is sortable */
  sortable?: boolean;
  /** Current sort direction for this column */
  sortDirection?: 'asc' | 'desc' | null;
  /** Current sort field key (for multi-option sorting) */
  sortField?: string | null;
  /** Sort change handler - sortKey is provided for multi-option sorting */
  onSortChange?: (
    columnId: string,
    direction: 'asc' | 'desc',
    sortKey?: string,
  ) => void;
  /** Custom labels for sort options */
  sortLabels?: { asc: string; desc: string };
  /** Invert arrow direction for semantic display (e.g., ↓ = earliest for dates) */
  invertArrows?: boolean;
  /** Multiple sort options for columns that can be sorted by different fields */
  sortOptions?: Array<{ key: string; ascLabel: string; descLabel: string }>;
  /** Date range filter change handler (required for daterange type) */
  onDateRangeFilterChange?: (
    startKey: string,
    endKey: string,
    startDate?: string,
    endDate?: string,
  ) => void;
  /** All current filter values (required for daterange to read start/end values) */
  allFilterValues?: FilterValues;
}

/**
 * Column filter component - routes to appropriate filter UI based on type.
 *
 * Filter types:
 * - select: Single-choice dropdown
 * - multiselect: Multi-choice with checkboxes
 * - text: Text input with apply button
 * - daterange: Delegates to DateRangeColumnFilter
 */
export const ColumnFilter = ({
  columnId,
  header,
  filter,
  value,
  onChange,
  sortable = false,
  sortDirection = null,
  sortField = null,
  onSortChange,
  sortLabels = { asc: 'A to Z', desc: 'Z to A' },
  invertArrows = false,
  sortOptions,
  onDateRangeFilterChange,
  allFilterValues = {},
}: ColumnFilterProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [asyncOptions, setAsyncOptions] = useState<FilterOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [textInputValue, setTextInputValue] = useState('');

  // Load async options if provided (one-time on mount)
  useEffect(() => {
    if (filter.loadOptions) {
      setIsLoading(true);
      filter
        .loadOptions()
        .then((loadedOptions) => {
          setAsyncOptions(loadedOptions);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [filter]);

  // Memoize options to prevent unnecessary re-renders
  const options = useMemo(
    () => (filter.loadOptions ? asyncOptions : filter.options || []),
    [filter.loadOptions, asyncOptions, filter.options],
  );

  // Initialize text input from current value
  useEffect(() => {
    if (filter.type === 'text' && typeof value === 'string') {
      setTextInputValue(value);
    }
  }, [value, filter.type]);

  // Check if filter is active
  const isFiltered = useMemo(() => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  }, [value]);

  // Get filter count for badge
  const filterCount = useMemo(() => {
    if (!isFiltered) return 0;
    if (Array.isArray(value)) return value.length;
    return 1;
  }, [isFiltered, value]);

  // Normalize value to array (URL params store single values as strings, multi as arrays)
  // Used by: handleMultiSelectToggle, handlePresetSelect, preset render, options render
  const normalizedValues = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'string') return [value];
    return [];
  }, [value]);

  // Filter options based on search (sorting is handled by column config via getSortedOptions)
  const filteredOptions = useMemo(() => {
    if (!searchQuery || !filter.searchable) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery, filter.searchable]);

  // Handlers for different filter types
  const handleSelectChange = useCallback(
    (optionValue: string | null) => {
      onChange(columnId, optionValue);
    },
    [columnId, onChange],
  );

  const handleMultiSelectToggle = useCallback(
    (optionValue: string) => {
      const newValues = normalizedValues.includes(optionValue)
        ? normalizedValues.filter((v) => v !== optionValue)
        : [...normalizedValues, optionValue];
      onChange(columnId, newValues.length > 0 ? newValues : null);
    },
    [columnId, normalizedValues, onChange],
  );

  const handleTextApply = useCallback(() => {
    onChange(columnId, textInputValue || null);
  }, [columnId, textInputValue, onChange]);

  const handleClear = useCallback(() => {
    onChange(columnId, null);
    setSearchQuery('');
    setTextInputValue('');
  }, [columnId, onChange]);

  // Handler for sort change
  const handleSortChange = useCallback(
    (direction: 'asc' | 'desc', sortKey?: string) => {
      onSortChange?.(columnId, direction, sortKey);
    },
    [columnId, onSortChange],
  );

  // Handler for "Select all" action
  const handleSelectAll = useCallback(() => {
    if (filter.type !== 'multiselect') return;
    const allValues = filteredOptions.map((opt) => opt.value);
    onChange(columnId, allValues);
  }, [filter.type, filteredOptions, columnId, onChange]);

  // Generic preset toggle handler - used by both multiselect and daterange
  const handlePresetToggle = useCallback(
    (presetValues: string[], isCurrentlyActive: boolean, filterKey: string) => {
      onChange(filterKey, isCurrentlyActive ? null : presetValues);
    },
    [onChange],
  );

  // Render daterange filter - delegated to self-contained component
  if (filter.type === 'daterange') {
    if (!filter.dateRangeConfig) {
      throw new Error(
        `ColumnFilter: daterange filter type requires dateRangeConfig. Column: ${columnId}`,
      );
    }

    if (!onDateRangeFilterChange) {
      throw new Error(
        `ColumnFilter: daterange filter type requires onDateRangeFilterChange handler. Column: ${columnId}`,
      );
    }

    return (
      <DateRangeColumnFilter
        columnId={columnId}
        header={header}
        filter={filter}
        dateRangeConfig={filter.dateRangeConfig}
        onChange={onChange}
        onDateRangeFilterChange={onDateRangeFilterChange}
        allFilterValues={allFilterValues ?? {}}
        sortable={sortable}
        sortDirection={sortDirection}
        onSortChange={onSortChange}
        sortLabels={sortLabels}
        invertArrows={invertArrows}
      />
    );
  }

  // Render text filter
  if (filter.type === 'text') {
    return (
      <Dropdown>
        <DropdownButton as={ColumnFilterTrigger}>
          <ColumnFilterHeader
            header={header}
            filterCount={filterCount}
            sortDirection={sortDirection}
            isFiltered={isFiltered}
            invertArrows={invertArrows}
            sortable={sortable}
          />
        </DropdownButton>

        <DropdownMenu className="min-w-[250px] block!" anchor="bottom start">
          {/* Sorting section */}
          {sortable && (
            <SortingSection
              sortDirection={sortDirection}
              sortField={sortField}
              sortLabels={sortLabels}
              onSortChange={handleSortChange}
              invertArrows={invertArrows}
              sortOptions={sortOptions}
            />
          )}

          {/* Filter section */}
          <div className="p-3 space-y-3">
            <input
              type="text"
              placeholder={
                filter.placeholder ||
                (typeof header === 'string'
                  ? `Filter ${header.toLowerCase()}...`
                  : 'Filter...')
              }
              value={textInputValue}
              onChange={(e) => setTextInputValue(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  handleTextApply();
                }
              }}
              className="h-8 w-full px-3 text-xs rounded-lg border border-zinc-950/10 bg-white focus:outline-none focus:border-zinc-950/20 dark:bg-zinc-900 dark:border-white/10 dark:text-white dark:focus:border-white/20"
            />
            <div className="flex gap-2">
              <Button onClick={handleTextApply} color="blue" className="flex-1">
                Apply
              </Button>
              {isFiltered && (
                <Button onClick={handleClear} color="zinc" className="flex-1">
                  Clear
                </Button>
              )}
            </div>
          </div>
        </DropdownMenu>
      </Dropdown>
    );
  }

  // Render select/multiselect filter
  const isMultiSelect = filter.type === 'multiselect';
  const showSelectAll =
    isMultiSelect &&
    filter.selectAllEnabled !== false &&
    filteredOptions.length > 0;

  return (
    <Dropdown>
      <DropdownButton as={ColumnFilterTrigger}>
        <ColumnFilterHeader
          header={header}
          filterCount={filterCount}
          sortDirection={sortDirection}
          isFiltered={isFiltered}
          invertArrows={invertArrows}
          sortable={sortable}
        />
      </DropdownButton>

      <DropdownMenu
        className="min-w-[200px] max-w-[280px] max-h-[400px] block!"
        anchor="bottom start"
      >
        {/* Sorting section */}
        {sortable && (
          <SortingSection
            sortDirection={sortDirection}
            sortField={sortField}
            sortLabels={sortLabels}
            onSortChange={handleSortChange}
            invertArrows={invertArrows}
            sortOptions={sortOptions}
          />
        )}

        {/* Filter section header with "Select all" */}
        <FilterSectionHeader
          showSelectAll={showSelectAll}
          onSelectAll={handleSelectAll}
        />

        {/* Search input */}
        {filter.searchable !== false && (
          <>
            <div className="px-2 pb-2 pt-2">
              <InputGroup>
                <MagnifyingGlassIcon data-slot="icon" />
                <input
                  type="text"
                  placeholder="Search Filters"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="h-8 w-full pl-9 pr-3 text-xs rounded-lg border border-zinc-950/10 bg-white focus:outline-none focus:border-zinc-950/20 dark:bg-zinc-900 dark:border-white/10 dark:text-white dark:focus:border-white/20"
                />
              </InputGroup>
            </div>
            {/* Removed divider after search per design screenshot */}
          </>
        )}

        {/* Presets for quick filtering */}
        {isMultiSelect && filter.presets && filter.presets.length > 0 && (
          <>
            <PresetChips
              presets={filter.presets}
              activeValues={normalizedValues}
              onToggle={(values, isActive) =>
                handlePresetToggle(values, isActive, columnId)
              }
              className="px-2 pb-2"
            />
            <DropdownDivider />
          </>
        )}

        {/* Options list */}
        <div className="max-h-[250px] overflow-y-auto">
          {isLoading && (
            <div className="px-3 py-2 text-xs text-gray-500">
              Loading options...
            </div>
          )}

          {!isLoading && filteredOptions.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-500">
              No options found
            </div>
          )}

          {!isLoading &&
            filteredOptions.length > 0 &&
            filteredOptions.map((option) => {
              const isSelected = normalizedValues.includes(option.value);

              // For multiselect, use a div with stopPropagation to keep menu open
              if (isMultiSelect) {
                return (
                  <div
                    key={option.value}
                    role="menuitem"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleMultiSelectToggle(option.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        e.preventDefault();
                        handleMultiSelectToggle(option.value);
                      }
                    }}
                    className="group cursor-pointer rounded-lg px-3.5 py-2.5 focus:outline-hidden sm:px-3 sm:py-1.5 text-left text-xs text-zinc-950 dark:text-white hover:bg-gray-50 dark:hover:bg-zinc-800 flex items-center gap-3 w-full"
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => {}} // Handled by parent div click
                      color="zinc" // Use zinc or default to match design
                      className="pointer-events-none size-4" // Prevent checkbox from stealing clicks
                    />
                    <span className="w-full truncate" title={option.label}>
                      {option.label}
                    </span>
                  </div>
                );
              }

              // For single-select, use DropdownItem which auto-closes
              return (
                <DropdownItem
                  key={option.value}
                  onClick={() => handleSelectChange(option.value)}
                  className="w-full flex! flex-row justify-start"
                >
                  {isSelected && <Check className="size-4" data-slot="icon" />}
                  <DropdownLabel
                    className="w-full truncate"
                    title={option.label}
                  >
                    {option.label}
                  </DropdownLabel>
                </DropdownItem>
              );
            })}
        </div>

        {/* Clear button */}
        {isFiltered && (
          <>
            <DropdownDivider />
            <DropdownItem
              onClick={handleClear}
              className="w-full flex! flex-row justify-center"
            >
              <X className="size-4" data-slot="icon" />
              <DropdownLabel>Clear filter</DropdownLabel>
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};
